import { useCallback, useContext } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
    isInsuranceOfferType,
    isSelfPayOfferType,
} from '@phx/data-sources/medication-marketplace';

import type {
    AuthorizedMedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { isValidPathway } from '../../util/is-valid-pathway';
import { useMedicationConfigOverride } from '../DrugProgramProvider';

import {
    type FillPathway,
    FillPrescriptionContext,
    type FillPrescriptionCtx,
    type UseFillPathwayData,
} from './shared';

export const useFillPathway = (): UseFillPathwayData => {
    const navigate = useNavigate();
    const { drxId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const pathway = searchParams.get('pathway') ?? '';
    const originalPathway = searchParams.get('original-pathway') ?? '';
    const [previousPathway, previousSubPathway] = (
        searchParams.get('previous-pathway') ?? ''
    ).split('|');
    const subPathway = searchParams.get('sub-pathway') ?? '';

    const setPathway = useCallback(
        (options: { pathway: FillPathway; isUserOverride?: boolean }) => {
            if (!drxId) {
                return;
            }

            const isOriginalPathway = options.pathway === originalPathway;

            // If not specified, assume it's a user override
            if (options.isUserOverride ?? true) {
                if (isOriginalPathway) {
                    navigate({
                        pathname: getAbsoluteRoute('fill.drxId.root', {
                            drxId,
                        }),
                        search: `?pathway=${options.pathway}`,
                    });
                } else {
                    navigate({
                        pathname: getAbsoluteRoute('fill.drxId.root', {
                            drxId,
                        }),
                        search: `?pathway=${options.pathway}&original-pathway=${pathway}`,
                    });
                }

                return;
            }

            setSearchParams({ pathway: options.pathway }, { replace: true });
        },
        [drxId, navigate, originalPathway, pathway, setSearchParams]
    );

    return {
        pathway: isValidPathway(pathway) ? pathway : null,
        originalPathway: isValidPathway(originalPathway)
            ? originalPathway
            : null,
        previousPathway: isValidPathway(previousPathway)
            ? previousPathway
            : null,
        subPathway: subPathway || null,
        previousSubPathway:
            isValidPathway(previousPathway) && !!previousSubPathway
                ? previousSubPathway
                : null,
        setPathway,
        isPathwayOverridden: !!originalPathway,
    };
};

export const useFillPrescription = (): FillPrescriptionCtx => {
    const ctx = useContext(FillPrescriptionContext);

    if (ctx === null) {
        throw new Error(
            'useFillPrescription must be called within a FillPrescriptionProvider'
        );
    }

    return ctx;
};

export const useHeadlinePrices = (
    medication:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment
        | undefined
) => {
    const medConfig = useMedicationConfigOverride();

    if (!medication) {
        return [];
    }

    const ndc =
        medication.__typename === 'UnknownMedication'
            ? medication.originalNdc || medication.id
            : medication.id;

    return medConfig.ndcInfo[ndc]?.headlinePrices ?? [];
};

export const useAvailablePathways = (): FillPathway[] => {
    const { allOffers } = useFillPrescription();

    const pathways: FillPathway[] = ['fallback'];

    if (
        allOffers.some((offer) => isInsuranceOfferType(offer.headlinePriceType))
    ) {
        pathways.push('insurance');
    }

    if (
        allOffers.some((offer) => isSelfPayOfferType(offer.headlinePriceType))
    ) {
        pathways.push('self-pay');
    }

    return pathways;
};
